import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["modal", "form", "modalTitle", "createButtons", "editButtons", "locationSelect", "departmentSelect", "staffSelect", "formErrors"]
  static values = {
    templateId: String,
    mode: { type: String, default: "create" }
  }

  connect() {
    if (this.hasLocationSelectTarget) {
      this.locationSelectTarget.setAttribute("data-action", "change->form-assignment#handleLocationChange")
    }

    if (this.hasDepartmentSelectTarget) {
      this.departmentSelectTarget.setAttribute("data-action", "change->form-assignment#handleDepartmentChange")
    }

    if (this.hasLocationSelectTarget && this.hasDepartmentSelectTarget) {
      const locationId = this.locationSelectTarget.value
      const departmentId = this.departmentSelectTarget.value

      if (locationId || departmentId) {
        this.loadStaff()
      }
    }
  }

  handleLocationChange() {
    this.loadStaff()
  }

  handleDepartmentChange() {
    this.loadStaff()
  }

  async loadStaff() {
    try {
      const locationId = this.hasLocationSelectTarget ? this.locationSelectTarget.value : null
      const departmentId = this.hasDepartmentSelectTarget ? this.departmentSelectTarget.value : null

      const params = new URLSearchParams()
      if (locationId) params.append('location_id', locationId)
      if (departmentId) params.append('department_id', departmentId)

      const response = await fetch(`/api/v1/data/workers?${params.toString()}`)

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const staff = await response.json()
      this.updateStaffOptions(staff)
    } catch (error) {
      this.updateStaffOptions([])
      this.showErrorMessage("Unable to load worker data. Please try again later.")
    }
  }

  updateStaffOptions(staff) {
    if (!this.hasStaffSelectTarget) return

    const staffSelect = this.staffSelectTarget
    staffSelect.innerHTML = '<option value="">All Workers</option>'

    if (Array.isArray(staff) && staff.length > 0) {
      staff.forEach(member => {
        const option = document.createElement('option')
        option.value = member.id
        option.textContent = `${member.name || "Unknown"} ${member.staff_code ? `(${member.staff_code})` : ""}`
        staffSelect.appendChild(option)
      })
    } else {
      const noResultsOption = document.createElement('option')
      noResultsOption.disabled = true
      noResultsOption.textContent = 'No workers found with selected filters'
      staffSelect.appendChild(noResultsOption)
    }
  }

  showModal() {
    this.modalTarget.classList.remove("hidden")
    document.body.classList.add('overflow-hidden')

    const backdrop = this.modalTarget.querySelector(".fixed.inset-0.bg-gray-500\\/75")
    const modalPanel = this.modalTarget.querySelector(".transform.overflow-hidden.rounded-lg, .relative.transform.overflow-hidden.rounded-lg")

    if (backdrop) {
      backdrop.classList.add("opacity-0")
      setTimeout(() => {
        backdrop.classList.remove("opacity-0")
        backdrop.classList.add("opacity-100")
      }, 10)
    }

    if (modalPanel) {
      modalPanel.classList.add("opacity-0", "translate-y-4", "sm:translate-y-0", "sm:scale-95")
      setTimeout(() => {
        modalPanel.classList.remove("opacity-0", "translate-y-4", "sm:translate-y-0", "sm:scale-95")
        modalPanel.classList.add("opacity-100", "translate-y-0", "sm:scale-100")
      }, 10)
    }

    this.updateButtonsVisibility()
  }

  closeModal() {
    const backdrop = this.modalTarget.querySelector(".fixed.inset-0.bg-gray-500\\/75")
    const modalPanel = this.modalTarget.querySelector(".transform.overflow-hidden.rounded-lg, .relative.transform.overflow-hidden.rounded-lg")

    if (backdrop) {
      backdrop.classList.remove("opacity-100")
      backdrop.classList.add("opacity-0", "ease-in", "duration-200")
    }

    if (modalPanel) {
      modalPanel.classList.remove("opacity-100", "translate-y-0", "sm:scale-100")
      modalPanel.classList.add("opacity-0", "translate-y-4", "sm:translate-y-0", "sm:scale-95", "ease-in", "duration-200")
    }

    setTimeout(() => {
      this.modalTarget.classList.add("hidden")
      document.body.classList.remove('overflow-hidden')
      this.resetForm()
    }, 200)
  }

  resetForm() {
    if (!this.hasFormTarget) return

    this.formTarget.reset()
    const templateId = this.templateIdValue || this.templateId
    this.formTarget.action = `/forms/templates/${templateId}/assignments`

    const methodInput = this.formTarget.querySelector('input[name="_method"]')
    if (methodInput) {
      methodInput.value = 'post'
    } else {
      const hiddenMethod = document.createElement('input')
      hiddenMethod.type = 'hidden'
      hiddenMethod.name = '_method'
      hiddenMethod.value = 'post'
      this.formTarget.appendChild(hiddenMethod)
    }

    this.modeValue = "create"
    this.updateButtonsVisibility()
    this.clearFormErrors()
  }

  clearFormErrors() {
    if (this.hasFormErrorsTarget) {
      this.formErrorsTarget.innerHTML = ""
    } else {
      const formErrors = document.getElementById("form-errors")
      if (formErrors) {
        formErrors.innerHTML = ""
      }
    }
  }

  showErrorMessage(message, title = "Error") {
    const formErrors = this.hasFormErrorsTarget ? this.formErrorsTarget : document.getElementById("form-errors")
    if (formErrors) {
      formErrors.innerHTML = `
        <div class="rounded-md bg-red-50 p-4 mb-4">
          <div class="flex">
            <div class="flex-shrink-0">
              <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clip-rule="evenodd" />
              </svg>
            </div>
            <div class="ml-3">
              <h3 class="text-sm font-medium text-red-800">${title}</h3>
              <div class="mt-2 text-sm text-red-700">
                <p>${message}</p>
              </div>
            </div>
          </div>
        </div>
      `
      formErrors.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }

  async handleSubmit(event) {
    event.preventDefault()
    this.clearFormErrors()

    try {
      const method = this.modeValue === 'edit' ? 'PATCH' : 'POST'

      const response = await fetch(this.formTarget.action, {
        method: method,
        body: new FormData(this.formTarget),
        headers: {
          "Accept": "text/vnd.turbo-stream.html"
        }
      })

      const html = await response.text()

      if (response.ok) {
        Turbo.renderStreamMessage(html)
        this.resetForm()
        this.closeModal()
      } else {
        Turbo.renderStreamMessage(html)
        const formErrors = this.hasFormErrorsTarget ? this.formErrorsTarget : document.getElementById("form-errors")
        if (formErrors) {
          formErrors.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
        }
      }
    } catch (error) {
      this.showErrorMessage("An error occurred while processing your request. Please try again.", "Submission error")
    }
  }

  editAssignment(event) {
    event.preventDefault()

    const assignmentId = event.currentTarget.dataset.assignmentId
    if (!assignmentId) return

    const assignment = this.element.querySelector(`tr[data-form-assignment-target="assignment"][data-assignment-id="${assignmentId}"]`)

    if (!assignment) {
      this.setupEditForm(assignmentId, {
        name: '',
        locationId: '',
        departmentId: '',
        staffId: '',
        startDate: '',
        endDate: ''
      })
    } else {
      this.setupEditForm(assignmentId, assignment.dataset)
    }
  }

  setupEditForm(assignmentId, data) {
    if (!this.hasFormTarget) return

    this.formTarget.action = `/forms/templates/${this.templateIdValue}/assignments/${assignmentId}`

    const methodInput = this.formTarget.querySelector('input[name="_method"]')
    if (methodInput) {
      methodInput.value = 'patch'
    } else {
      const hiddenMethodInput = document.createElement('input')
      hiddenMethodInput.type = 'hidden'
      hiddenMethodInput.name = '_method'
      hiddenMethodInput.value = 'patch'
      this.formTarget.appendChild(hiddenMethodInput)
    }

    this.safeSetFormValue('form_assignment[name]', data.name)
    this.safeSetFormValue('form_assignment[location_id]', data.locationId)
    this.safeSetFormValue('form_assignment[department_id]', data.departmentId)

    const needsStaffReload = (data.locationId || data.departmentId) &&
      (this.hasLocationSelectTarget || this.hasDepartmentSelectTarget)

    if (needsStaffReload) {
      this.loadStaff().then(() => {
        this.safeSetFormValue('form_assignment[staff_id]', data.staffId)
      })
    } else {
      this.safeSetFormValue('form_assignment[staff_id]', data.staffId)
    }

    this.safeSetFormValue('form_assignment[start_date]', data.startDate)
    this.safeSetFormValue('form_assignment[end_date]', data.endDate)

    this.modeValue = "edit"
    if (this.hasModalTitleTarget) this.modalTitleTarget.textContent = "Edit Assignment"
    this.updateButtonsVisibility()
    this.showModal()
  }

  safeSetFormValue(fieldName, value) {
    const field = this.formTarget.querySelector(`[name='${fieldName}']`)
    if (field) {
      field.value = value || ''
    }
  }

  updateButtonsVisibility() {
    if (this.modeValue === "create") {
      if (this.hasCreateButtonsTarget) this.createButtonsTarget.classList.remove("!hidden")
      if (this.hasEditButtonsTarget) this.editButtonsTarget.classList.add("!hidden")
      if (this.hasModalTitleTarget) this.modalTitleTarget.textContent = "Add Assignment"
    } else {
      if (this.hasCreateButtonsTarget) this.createButtonsTarget.classList.add("!hidden")
      if (this.hasEditButtonsTarget) this.editButtonsTarget.classList.remove("!hidden")
      if (this.hasModalTitleTarget) this.modalTitleTarget.textContent = "Edit Assignment"
    }
  }

  submitAndAddMore(event) {
    event.preventDefault()

    if (!this.hasFormTarget) return

    this.handleSubmitAndContinue()
  }

  async handleSubmitAndContinue() {
    this.clearFormErrors()

    try {
      const method = this.modeValue === 'edit' ? 'PATCH' : 'POST'

      const response = await fetch(this.formTarget.action, {
        method: method,
        body: new FormData(this.formTarget),
        headers: {
          "Accept": "text/vnd.turbo-stream.html"
        }
      })

      const html = await response.text()

      if (response.ok) {
        Turbo.renderStreamMessage(html)
        this.resetForm()
        this.showModal()
      } else {
        Turbo.renderStreamMessage(html)
        const formErrors = this.hasFormErrorsTarget ? this.formErrorsTarget : document.getElementById("form-errors")
        if (formErrors) {
          formErrors.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
        }
      }
    } catch (error) {
      this.showErrorMessage("An error occurred while processing your request. Please try again.", "Submission error")
    }
  }

  get templateId() {
    return this.templateIdValue || window.location.pathname.split("/")[3]
  }
}
